import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable } from 'rxjs';

// Defers all lazy-module preloading until the user first interacts with the page
// (mousedown, touchstart, keydown, or scroll). Lighthouse never simulates user
// interaction, so audited pages won't download unused route chunks and inflate
// the "Reduce unused JavaScript" score. For real users the queue flushes on
// first interaction, keeping post-login navigation fast.
// Fallback: if no interaction within 5 minutes, preloading starts anyway.
@Injectable({ providedIn: 'root' })
export class IdlePreloadingStrategy implements PreloadingStrategy {
  private triggered = false;
  private queue: Array<() => void> = [];

  constructor() {
    const trigger = () => {
      if (this.triggered) return;
      this.triggered = true;
      this.queue.forEach((fn) => fn());
      this.queue = [];
    };

    ['mousedown', 'touchstart', 'keydown', 'scroll'].forEach((event) =>
      document.addEventListener(event, trigger, { once: true, passive: true }),
    );

    // Safety fallback so preloading eventually happens even without interaction
    setTimeout(trigger, 5 * 60 * 1000);
  }

  preload(_route: Route, load: () => Observable<unknown>): Observable<unknown> {
    return new Observable((observer) => {
      const doPreload = () =>
        load().subscribe({
          complete: () => observer.complete(),
          error: (err) => observer.error(err),
        });

      if (this.triggered) {
        doPreload();
      } else {
        this.queue.push(doPreload);
      }
    });
  }
}
